import { useState, useCallback, useRef } from 'react';
import { Upload, X, Image, GripVertical, Plus, Link as LinkIcon, Loader2 } from 'lucide-react';
import { useAdminStore } from '../store/adminStore';

interface ImageUploaderProps {
  images: string[];
  onChange: (images: string[]) => void;
  maxImages?: number;
}

const API_BASE = () => (import.meta.env.VITE_API_URL || 'http://localhost:3001') + '/api/products';

export default function ImageUploader({ images, onChange, maxImages = 5 }: ImageUploaderProps) {
  const token = useAdminStore((state) => state.token);
  const [isDragging, setIsDragging] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadOneFile = async (file: File): Promise<string | null> => {
    const formData = new FormData();
    formData.append('image', file);
    try {
      const response = await fetch(`${API_BASE()}/upload-image.php`, {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        body: formData,
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || `Sunucu hatası (${response.status})`);
      }
      return data.url as string;
    } catch (err) {
      console.error('Görsel yüklenemedi:', err);
      setUploadError(err instanceof Error ? err.message : 'Görsel yüklenemedi');
      return null;
    }
  };

  const handleFiles = useCallback(async (files: FileList | null) => {
    if (!files) return;
    const remaining = maxImages - images.length;
    const filesToProcess = Array.from(files)
      .filter((f) => f.type.startsWith('image/'))
      .slice(0, remaining);

    if (filesToProcess.length === 0) return;

    setUploadError('');
    setUploading(true);

    const uploadedUrls: string[] = [];
    for (const file of filesToProcess) {
      const url = await uploadOneFile(file);
      if (url) uploadedUrls.push(url);
    }

    setUploading(false);
    if (uploadedUrls.length > 0) {
      onChange([...images, ...uploadedUrls]);
    }
  }, [images, maxImages, onChange, token]);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFiles(e.target.files);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleAddUrl = () => {
    const url = urlInput.trim();
    if (url && !images.includes(url) && images.length < maxImages) {
      onChange([...images, url]);
      setUrlInput('');
      setShowUrlInput(false);
    }
  };

  const handleRemove = (index: number) => {
    onChange(images.filter((_, i) => i !== index));
  };

  const handleReorder = (fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) return;
    const newImages = [...images];
    const [moved] = newImages.splice(fromIndex, 1);
    newImages.splice(toIndex, 0, moved);
    onChange(newImages);
  };

  const handleItemDragStart = (e: React.DragEvent, index: number) => {
    setDragIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleItemDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    setDragOverIndex(index);
  };

  const handleItemDrop = (e: React.DragEvent, toIndex: number) => {
    e.preventDefault();
    if (dragIndex !== null) {
      handleReorder(dragIndex, toIndex);
    }
    setDragIndex(null);
    setDragOverIndex(null);
  };

  return (
    <div className="space-y-3">
      {/* Upload Area */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => !uploading && fileInputRef.current?.click()}
        className={`relative border-2 border-dashed rounded-xl p-6 text-center transition-all duration-200 ${
          uploading ? 'cursor-wait opacity-75' : 'cursor-pointer'
        } ${
          isDragging
            ? 'border-brand-red bg-red-50'
            : 'border-gray-300 hover:border-brand-red/50 hover:bg-gray-50'
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleFileInput}
          className="hidden"
          disabled={uploading}
        />
        {uploading ? (
          <>
            <Loader2 className="w-8 h-8 mx-auto mb-2 text-brand-red animate-spin" />
            <p className="text-sm font-medium text-gray-700">Görsel sunucuya yükleniyor...</p>
          </>
        ) : (
          <>
            <Upload className={`w-8 h-8 mx-auto mb-2 ${isDragging ? 'text-brand-red' : 'text-gray-400'}`} />
            <p className="text-sm font-medium text-gray-700">
              Görselleri sürükle & bırak
            </p>
            <p className="text-xs text-gray-500 mt-1">
              veya tıklayarak seçin • JPG, PNG, WebP • Maks {maxImages} görsel
            </p>
          </>
        )}
      </div>

      {uploadError && (
        <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
          ⚠️ {uploadError}
        </p>
      )}

      {/* URL Input Toggle */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setShowUrlInput(!showUrlInput)}
          className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-brand-red transition-colors"
        >
          <LinkIcon className="w-3.5 h-3.5" />
          URL ile ekle
        </button>
        {images.length > 0 && (
          <span className="text-xs text-gray-400">
            {images.length}/{maxImages} görsel
          </span>
        )}
      </div>

      {/* URL Input */}
      {showUrlInput && (
        <div className="flex gap-2">
          <input
            type="url"
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAddUrl()}
            placeholder="https://ornek.com/gorsel.jpg"
            className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-red/20"
          />
          <button
            type="button"
            onClick={handleAddUrl}
            disabled={!urlInput.trim() || images.length >= maxImages}
            className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium disabled:opacity-50 transition-colors"
          >
            Ekle
          </button>
        </div>
      )}

      {/* Image Preview Grid */}
      {images.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {images.map((img, index) => (
            <div
              key={`${img.substring(0, 50)}-${index}`}
              draggable
              onDragStart={(e) => handleItemDragStart(e, index)}
              onDragOver={(e) => handleItemDragOver(e, index)}
              onDrop={(e) => handleItemDrop(e, index)}
              onDragEnd={() => { setDragIndex(null); setDragOverIndex(null); }}
              className={`relative group rounded-xl overflow-hidden border-2 transition-all duration-200 bg-gray-100 ${
                dragOverIndex === index
                  ? 'border-brand-red scale-105'
                  : dragIndex === index
                  ? 'border-brand-red opacity-50'
                  : 'border-transparent hover:border-gray-300'
              }`}
            >
              <div className="aspect-square">
                <img
                  src={img}
                  alt={`Ürün görseli ${index + 1}`}
                  className="w-full h-full object-contain p-1"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0xMDAgNzBMMTIwIDEyMEg4MEwxMDAgNzBaIiBmaWxsPSIjQ0Q1RkRGIi8+CjxyZWN0IHg9Ijg1IiB5PSIxMTAiIHdpZHRoPSIzMCIgaGVpZ2h0PSIyMCIgcng9IjMiIGZpbGw9IiNDRDVERkYiLz4KPC9zdmc+';
                  }}
                />
              </div>

              <div className="absolute top-1 left-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="p-1 bg-black/50 rounded-md cursor-grab active:cursor-grabbing">
                  <GripVertical className="w-3 h-3 text-white" />
                </div>
              </div>

              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); handleRemove(index); }}
                className="absolute top-1 right-1 p-1 bg-red-500 hover:bg-red-600 rounded-full text-white opacity-0 group-hover:opacity-100 transition-all shadow-lg"
              >
                <X className="w-3 h-3" />
              </button>

              {index === 0 && (
                <div className="absolute bottom-1 left-1 px-1.5 py-0.5 bg-brand-red text-white text-[10px] font-bold rounded-md">
                  Ana Görsel
                </div>
              )}

              <div className="absolute bottom-1 right-1 px-1.5 py-0.5 bg-black/50 text-white text-[10px] font-mono rounded-md">
                {index + 1}
              </div>
            </div>
          ))}

          {images.length < maxImages && (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="aspect-square rounded-xl border-2 border-dashed border-gray-300 hover:border-brand-red flex flex-col items-center justify-center gap-1 text-gray-400 hover:text-brand-red transition-colors"
            >
              <Plus className="w-6 h-6" />
              <span className="text-xs font-medium">Ekle</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
